// NOTE: Methods in this file are based on locale config and should NOT be used in business logic. Just formatting to be shown in templates.

import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';
import { t } from '@shared/helpers/translation';

const fullLocale = getCurrentMarketSpecs().fullLocale;

export function getLocaleDateTime(value: Date): string {
    //Output format: 18 januari 2024 kl. 00:00
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleDayShortMonthYear(value: Date): string {
    //Output format: 18 jan. 2024
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleDayShortMonthTime(value: Date): string {
    //Output format: 18 jan. 00:00
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleWeekdayLongMonthYear(value: Date): string {
    //Output format: torsdag 18 januari 2024
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleWeekdayShortMonthYear(value: Date): string {
    //Output format: torsdag 18 jan. 2024
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'long',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleDigitDate(value: Date): string {
    //Output format: Depends on locale, e.g. 2024-01-18 for Sweden and 18.01.2024 for Denmark. Possibly dependent on browser as well
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleDayShortMonth(value: Date): string {
    //Output format: 18 jan.
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleDigitDateTime(value: Date): string {
    //Output format: 2024-01-18 00:00
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    };
    return value.toLocaleDateString(fullLocale, options);
}

export function getLocaleIntlDateFormat(locale: string) {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    const parts = formatter.formatToParts(new Date(2000, 1, 1)); // Use a fixed date to get the format parts
    const formatString = parts
        .map((part) => {
            switch (part.type) {
                case 'year':
                    return 'yyyy';
                case 'month':
                    return 'MM';
                case 'day':
                    return 'dd';
                default:
                    return part.value;
            }
        })
        .join('');
    return formatString;
}

export function getTranslatedDateFormat(format: string) {
    return format
        .replace(/([Yy])/g, t({ key: 'date.format.year' }))
        .replace(/([Mm])/g, t({ key: 'date.format.month' }))
        .replace(/([Dd])/g, t({ key: 'date.format.day' }));
}

export function getLocaleIntlDatetimeFormat(locale: string) {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    const parts = formatter.formatToParts(new Date(2000, 1, 1, 5, 25)); // Use a fixed date to get the format parts
    const formatString = parts
        .map((part) => {
            switch (part.type) {
                case 'year':
                    return 'yyyy';
                case 'month':
                    return 'MM';
                case 'day':
                    return 'dd';
                case 'hour':
                    return 'HH';
                case 'minute':
                    return 'mm';
                default:
                    return part.value;
            }
        })
        .join('');

    return formatString;
}

export function getTranslatedDatetimeFormat(format: string) {
    return format
        .replace(/([Yy])/g, t({ key: 'date.format.year' }))
        .replace(/([M])/g, t({ key: 'date.format.month' }))
        .replace(/([Dd])/g, t({ key: 'date.format.day' }))
        .replace(/([Hh])/g, t({ key: 'date.format.hour' }))
        .replace(/([m])/g, t({ key: 'date.format.minute' }));
}

export function getTranslatedDateHint(date: Date, fullLocale: string): string {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };
    const formatter = new Intl.DateTimeFormat(fullLocale, options);

    return formatter.format(date);
}

export function getTranslatedDatetimeHint(date: Date, fullLocale: string): string {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    };
    const formatter = new Intl.DateTimeFormat(fullLocale, options);

    return formatter.format(date);
}

export function getLocaleMinutesDuration(
    minutes: number,
    fullLocale: string,
    unitDisplay: 'narrow' | 'short' | 'long' = 'long'
): string {
    const formatter = new Intl.NumberFormat(fullLocale, {
        style: 'unit',
        unit: 'minute',
        unitDisplay,
    });

    return formatter.format(minutes);
}
